import React from 'react';
import { useIntl } from 'react-intl';
import { shallowEqual, useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';

import config from '../../config';
import { closeBookmarkLoginRequiredModal } from '../../ducks/bookmarks.duck';
import { toggleContactModalState } from '../../ducks/contact.duck';
import { closePushNotificationModal } from '../../ducks/cordova.duck';
import { closeNewsletterModal, closeNewsletterSubscribeModal } from '../../ducks/newsletter.duck';
import { acknowledgePolicesChange } from '../../ducks/startUp.duck';
import { updateTransaction } from '../../ducks/transaction.duck';
import { manageDisableScrolling } from '../../ducks/ui.duck';
import { expiredOrderModalClose, fetchCurrentUser, sendVerificationEmail } from '../../ducks/user.duck';
import routeConfiguration from '../../routeConfiguration';
import useAppSelector from '../../util/hooks/useAppSelector';
import useScreenSize from '../../util/hooks/useScreenSize';
import { createResourceLocatorString } from '../../util/routes';
import { parse } from '../../util/urlHelpers';
import BookmarkLoginModal from './BookmarkLoginModal';
import ContactModal from './ContactModal';
import ExpiredOrderModal from './ExpiredOrderModal';
import MissingInformationModal from './MissingInformationModal';
import MobileSearchModal from './MobileMenu/MobileSearchModal';
import { NewsLetterModal } from './NewsletterModal';
import NewsletterSubscribedModal from './NewsletterSubscribedModal';
import PolicyHasChangedModal from './PolicyHasChangedModal';
import PushNotificationModal from './PushNotificationModal';
import VerifyPhoneNumberModal from './VerifyPhoneNumberModal';

const PortalModalContainer = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const currentUser = useAppSelector((state) => state.user.currentUser);
  const {
    currentUserExpiredOrder,
    currentUserExpiredOrderModalOpen,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
  } = useAppSelector(
    (state) => ({
      currentUserHasListings: state.user.currentUserHasListings,
      currentUserHasOrders: state.user.currentUserHasOrders,
      currentUserExpiredOrder: state.user.currentUserExpiredOrder,
      currentUserExpiredOrderModalOpen: state.user.currentUserExpiredOrderModalOpen,
      sendVerificationEmailInProgress: state.user.sendVerificationEmailInProgress,
      sendVerificationEmailError: state.user.sendVerificationEmailError,
    }),
    shallowEqual,
  );

  const { pushNotificationModalOpen, pushNotificationModalData } = useAppSelector(
    (state) => ({
      pushNotificationModalOpen: state.cordova.pushNotificationModalOpen,
      pushNotificationModalData: state.cordova.pushNotificationModalData,
    }),
    shallowEqual,
  );

  const bookmarkLoginRequiredModalOpen = useAppSelector(
    (state) => state.bookmarks.bookmarkLoginRequiredModalOpen,
  );

  const policiesChangedModalOpen = useAppSelector(
    (state) => state.startUp.policiesChangedModalOpen,
  );

  const {
    newsletterModalOpen,
    newsletterSubscribeModalOpen,
  } = useAppSelector(
    (state) => ({
      newsletterModalOpen: state.newsletter.newsletterModalOpen,
      newsletterSubscribeModalOpen: state.newsletter.newsletterSubscribeModalOpen,
    }),
    shallowEqual,
  );

  const contactModalOpen = useAppSelector((state) => state.contact.contactModalOpen);

  const onManageDisableScrolling = (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling));
  const onCloseBookmarkLoginRequiredModal = () => dispatch(closeBookmarkLoginRequiredModal());
  const onClosePushNotificationModal = () => dispatch(closePushNotificationModal());
  const onResendVerificationEmail = () => dispatch(sendVerificationEmail());
  const onReFetchCurrentUser = () => dispatch(fetchCurrentUser());
  const onUpdateTransaction = (params) => dispatch(updateTransaction(params));
  const onCloseNewsLetterModal = () => dispatch(closeNewsletterModal());

  // POLICES CHANGED MODAL
  const onClosePoliciesChangedModal = () => dispatch(acknowledgePolicesChange());

  // NEWSLETTER SUBSCRIBE MODAL
  const onCloseNewsletterSubscribeModal = () => dispatch(closeNewsletterSubscribeModal());

  // EXPIRED ORDER MODAL
  const onCloseExpiredOrderModal = () => dispatch(expiredOrderModalClose());

  const handleLoginFromBookmarkRequiredModal = () => {
    onCloseBookmarkLoginRequiredModal();

    if (!currentUser) {
      const state = { from: `${location.pathname}${location.search}${location.hash}` };
      navigate(
        createResourceLocatorString('SignupPage', routeConfiguration(), {}, {}, intl.locale),
        { state },
      );
    }
  };

  const handlePushNotificationButtonClick = () => {
    onClosePushNotificationModal();
    if (pushNotificationModalData.url) {
      navigate(pushNotificationModalData.url);
    }
  };

  const { mobileSearch, address, origin, bounds, mapSearch, page, ...searchInURL } = parse(
    location.search,
    {
      latlng: ['origin'],
      latlngBounds: ['bounds'],
    },
  );

  const { isMobile } = useScreenSize();
  const isMobileSearchOpen = isMobile && mobileSearch === 'open';

  const locationFieldsPresent = config.sortSearchByDistance
    ? address && origin && bounds
    : address && bounds;
  const initialSearchFormValues = {
    location: locationFieldsPresent
      ? {
        search: address,
        selectedPlace: { address, origin, bounds },
      }
      : null,
  };

  const handleExpiredOrderModalClosed = (showOrder) => {
    onUpdateTransaction({
      id: currentUserExpiredOrder?.id?.uuid,
      metadata: {
        customerNotificationModalShown: true,
      },
    });
    onCloseExpiredOrderModal();
    if (showOrder) {
      navigate(
        createResourceLocatorString(
          'OrderPage',
          routeConfiguration(),
          {
            id: currentUserExpiredOrder?.id?.uuid,
          },
          {},
          intl.locale,
        ),
      );
    }
  };

  return (
    <>
      <MobileSearchModal
        initialSearchFormValues={initialSearchFormValues}
        intl={intl}
        isMobileSearchOpen={isMobileSearchOpen}
        navigate={navigate}
        onManageDisableScrolling={onManageDisableScrolling}
        searchInURL={searchInURL}
      />
      <MissingInformationModal
        onReFetchCurrentUser={onReFetchCurrentUser}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailError={sendVerificationEmailError}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
      />
      <ExpiredOrderModal
        isOpen={currentUserExpiredOrderModalOpen}
        onAcknowledged={() => handleExpiredOrderModalClosed(true)}
        onClose={() => handleExpiredOrderModalClosed(false)}
        onManageDisableScrolling={onManageDisableScrolling}
      />
      <BookmarkLoginModal
        bookmarkLoginRequiredModalOpen={bookmarkLoginRequiredModalOpen}
        handleLoginFromBookmarkRequiredModal={handleLoginFromBookmarkRequiredModal}
        onCloseBookmarkLoginRequiredModal={onCloseBookmarkLoginRequiredModal}
        onManageDisableScrolling={onManageDisableScrolling}
      />
      <PolicyHasChangedModal
        isOpen={policiesChangedModalOpen}
        onButtonClick={onClosePoliciesChangedModal}
        onClose={onClosePoliciesChangedModal}
        onManageDisableScrolling={onManageDisableScrolling}
      />
      <PushNotificationModal
        handlePushNotificationButtonClick={handlePushNotificationButtonClick}
        onManageDisableScrolling={onManageDisableScrolling}
        pushNotificationModalData={pushNotificationModalData}
        pushNotificationModalOpen={pushNotificationModalOpen}
      />
      <NewsLetterModal
        isOpen={newsletterModalOpen}
        onClose={onCloseNewsLetterModal}
      />
      <NewsletterSubscribedModal
        isOpen={newsletterSubscribeModalOpen}
        onClose={onCloseNewsletterSubscribeModal}
        onManageDisableScrolling={onManageDisableScrolling}
      />
      <ContactModal
        isOpen={contactModalOpen}
        onClose={() => dispatch(toggleContactModalState(false))}
      />
      <VerifyPhoneNumberModal />
    </>
  );
};

export default PortalModalContainer;
